// src/pages/CheckoutAddress.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./CheckoutPage.css";

export default function CheckoutAddress() {
  const navigate = useNavigate();
  const { cartTotal, memberSavings } = useCart();
  const [form, setForm] = useState({
    name: "", phone: "", pincode: "", address: "", locality: "", city: "", state: "", type: "Home",
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required";
    if (!/^\d{10}$/.test(form.phone)) e.phone = "Enter valid 10-digit mobile";
    if (!/^\d{6}$/.test(form.pincode)) e.pincode = "Enter valid 6-digit pincode";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state.trim()) e.state = "State is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: "" }));
  };

  const handleNext = () => { if (validate()) navigate("/checkout/payment"); };

  return (
    <div className="checkout-page">
      <div className="cart-breadcrumb">
        <span className="cart-breadcrumb__step">MY BAG</span>
        <span className="cart-breadcrumb__line" />
        <span className="cart-breadcrumb__step cart-breadcrumb__step--active">ADDRESS</span>
        <span className="cart-breadcrumb__line" />
        <span className="cart-breadcrumb__step">PAYMENT</span>
      </div>

      <div className="checkout-layout container">
        <div className="checkout-form-wrap">
          <h2 className="checkout-section-title">Delivery Address</h2>

          <div className="checkout-type-btns">
            {["Home", "Work", "Other"].map(t => (
              <button key={t}
                className={`checkout-type-btn ${form.type === t ? "checkout-type-btn--active" : ""}`}
                onClick={() => handleChange("type", t)}>
                {t}
              </button>
            ))}
          </div>

          <div className="checkout-grid">
            <FormField label="Full Name *" error={errors.name}>
              <input placeholder="Enter your full name" value={form.name} onChange={e => handleChange("name", e.target.value)} />
            </FormField>
            <FormField label="Mobile Number *" error={errors.phone}>
              <input placeholder="10-digit mobile number" value={form.phone} onChange={e => handleChange("phone", e.target.value)} maxLength={10} />
            </FormField>
            <FormField label="Pincode *" error={errors.pincode}>
              <input placeholder="6-digit pincode" value={form.pincode} onChange={e => handleChange("pincode", e.target.value)} maxLength={6} />
            </FormField>
            <FormField label="City *" error={errors.city}>
              <input placeholder="City" value={form.city} onChange={e => handleChange("city", e.target.value)} />
            </FormField>
            <FormField label="Address (House No, Street, Area) *" error={errors.address} fullWidth>
              <input placeholder="Flat no, building, street, area" value={form.address} onChange={e => handleChange("address", e.target.value)} />
            </FormField>
            <FormField label="Locality / Town" fullWidth>
              <input placeholder="Locality or nearby landmark" value={form.locality} onChange={e => handleChange("locality", e.target.value)} />
            </FormField>
            <FormField label="State *" error={errors.state}>
              <select value={form.state} onChange={e => handleChange("state", e.target.value)}>
                <option value="">Select State</option>
                {["Tamil Nadu","Maharashtra","Delhi","Karnataka","Telangana","West Bengal","Gujarat","Rajasthan","Uttar Pradesh","Kerala","Punjab","Haryana","Andhra Pradesh","Madhya Pradesh"].map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </FormField>
          </div>

          <button className="checkout-continue-btn" onClick={handleNext}>
            CONTINUE TO PAYMENT
          </button>
        </div>

        {/* Order summary */}
        <OrderSummary cartTotal={cartTotal} memberSavings={memberSavings} />
      </div>
    </div>
  );
}

function FormField({ label, error, children, fullWidth }) {
  return (
    <div className={`form-field ${fullWidth ? "form-field--full" : ""}`}>
      <label className="form-label">{label}</label>
      {React.cloneElement(children, { className: `form-input ${error ? "form-input--error" : ""}` })}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

export function OrderSummary({ cartTotal, memberSavings }) {
  const { items } = useCart();
  const selectedItems = items.filter(i => i.selected !== false);
  const total = selectedItems.reduce((s, i) => s + i.price * i.qty, 0);
  const mrp = selectedItems.reduce((s, i) => s + i.mrp * i.qty, 0);
  const memSave = Math.round(total * 0.09);
  const shipping = total > 445 ? 0 : 50;
  const finalTotal = total - memSave + shipping;

  return (
    <div className="checkout-summary">
      <h3 className="checkout-summary__title">ORDER SUMMARY</h3>
      <div className="checkout-summary__items">
        {items.slice(0, 3).map(item => (
          <div key={item.key} className="checkout-summary__item">
            <img src={item.img} alt={item.name} className="checkout-summary__img" />
            <div>
              <p className="checkout-summary__item-name">{item.name}</p>
              <p className="checkout-summary__item-meta">Size: {item.selectedSize} · Qty: {item.qty}</p>
              <p className="checkout-summary__item-price">₹ {(item.price * item.qty).toLocaleString()}</p>
            </div>
          </div>
        ))}
        {items.length > 3 && <p className="checkout-summary__more">+{items.length - 3} more item(s)</p>}
      </div>
      <div className="checkout-summary__billing">
        <div className="checkout-billing-row"><span>Cart Total</span><span>₹ {mrp.toLocaleString()}</span></div>
        {memSave > 0 && <div className="checkout-billing-row checkout-billing-row--teal"><span>Member Savings</span><span>- ₹ {memSave.toLocaleString()}</span></div>}
        <div className="checkout-billing-row"><span>Shipping</span><span style={{ color: "var(--tss-teal)" }}>{shipping === 0 ? "FREE" : `₹ ${shipping}`}</span></div>
        <div className="checkout-billing-row checkout-billing-row--total"><span>Total Amount</span><span>₹ {finalTotal.toLocaleString()}.00</span></div>
      </div>
    </div>
  );
}
